
import React from "react";
import {
  MessageCircle,
  Quote,
} from "lucide-react";

const DirectorsDesk = () => {
  return (
    <section className="relative w-full overflow-hidden bg-[#FFFCF8] py-8 sm:py-10 lg:py-12">

      {/* Background Decoration */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-28 top-10 h-64 w-64 rounded-full bg-red-100/60 blur-3xl" />

        <div className="absolute -right-28 bottom-0 h-72 w-72 rounded-full bg-amber-100/60 blur-3xl" />

        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              "radial-gradient(#991b1b 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1600px] px-5 sm:px-8 lg:px-12 xl:px-16">

        {/* SECTION HEADING */}
        <div className="mb-8 text-center">

          <h2 className="font-serif text-[30px] font-black leading-tight tracking-tight text-slate-950 sm:text-[38px] lg:text-[46px]">
            Message from the{" "}
            <span className="relative inline-block text-red-800">
              Director

              <span className="absolute -bottom-2 left-0 h-1 w-full rounded-full bg-gradient-to-r from-red-700 to-amber-400" />
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-[13px] leading-relaxed text-slate-500 sm:text-[15px]">
            A vision for quality education, strong values
            and a future filled with opportunities.
          </p>
        </div>

        {/* TWO COLUMN LAYOUT */}
        <div className="grid items-start gap-8 lg:grid-cols-[1.12fr_0.88fr] lg:gap-10">

          {/* LEFT CONTENT */}
          <div>

            {/* SMALL HEADING */}
            <div className="mb-4 flex items-center gap-3">
              <span className="h-[3px] w-9 rounded-full bg-amber-500" />

              <p className="text-[11px] font-black uppercase tracking-[0.15em] text-amber-700">
                A Message of Purpose & Progress
              </p>
            </div>

            {/* GREETING */}
            <div className="relative overflow-hidden rounded-2xl border border-red-100 bg-white px-5 py-4 shadow-sm">

              <div className="absolute bottom-0 left-0 top-0 w-1 bg-red-700" />

              <div className="flex items-center gap-4">

                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-800">
                  <MessageCircle size={21} />
                </div>

                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-red-700">
                    Director&apos;s Message
                  </p>

                  <h3 className="mt-1 font-serif text-lg font-bold text-slate-950 sm:text-xl">
                    Dear Parents and Students,
                  </h3>
                </div>

              </div>
            </div>

            {/* DIRECTOR MESSAGE */}
            <div className="mt-5 space-y-3.5 text-[13px] leading-[1.8] text-slate-600 sm:text-[14px]">

              <p>
                Down through the years,{" "}
                <strong className="text-red-800">
                  St. Xavier&apos;s School
                </strong>{" "}
                has been a torchbearer and trendsetter,
                responding to the changing demands of
                science, technology and modern education.
                We encourage our students to discover
                their own path through determination,
                knowledge and self-discipline.
              </p>

              <p>
                Our commitment extends beyond academic
                achievement. We believe in the all-round
                development of every child through
                meaningful classroom learning,
                co-curricular activities and a positive
                school environment where students and
                teachers continue to grow.
              </p>

            </div>

            {/* HIGHLIGHT QUOTE */}
            <div className="relative my-5 overflow-hidden rounded-2xl bg-gradient-to-r from-red-800 to-red-700 px-5 py-4 text-center shadow-md">

              <div className="pointer-events-none absolute -right-6 -top-7 h-24 w-24 rounded-full border-[12px] border-white/10" />

              <Quote
                size={22}
                className="mx-auto mb-2 text-amber-300"
              />

              <p className="relative font-serif text-lg font-bold italic text-white sm:text-xl">
                “I see it, I get it, I can do it.”
              </p>

            </div>

            {/* CLOSING MESSAGE */}
            <div className="space-y-3 text-[13px] leading-[1.8] text-slate-600 sm:text-[14px]">

              <p>
                At St. Xavier&apos;s School, effective
                planning, professional development,
                student guidance and continuous improvement
                are integral to our approach. Students are
                encouraged to participate in debates,
                quizzes, cultural programmes and
                CBSE cluster competitions.
              </p>

              <p>
                We strive to prepare every learner with
                confidence, discipline, innovative thinking
                and strong values to succeed in life and
                contribute meaningfully to society.
              </p>

            </div>

            {/* TAGORE QUOTE */}
            <div className="mt-5 rounded-xl border-l-4 border-amber-400 bg-white px-5 py-4 shadow-sm">

              <p className="font-serif text-[13px] italic leading-relaxed text-slate-700 sm:text-sm">
                “Where the mind is without fear and the head
                is held high, where knowledge is free...”
              </p>

              <p className="mt-2 text-[11px] font-bold uppercase tracking-wide text-red-800">
                — Rabindranath Tagore
              </p>

            </div>

          </div>

          {/* RIGHT SIDE - DIRECTOR IMAGE */}
          <div className="relative mx-auto w-full max-w-[650px]">

            {/* Decorative Golden Corner */}
            <div className="pointer-events-none absolute -right-3 -top-3 h-32 w-32 rounded-[30px] border-[8px] border-amber-300 sm:-right-4 sm:-top-4" />

            {/* Decorative Red Corner */}
            <div className="pointer-events-none absolute -bottom-4 -left-3 h-32 w-32 rounded-[30px] bg-red-800 sm:-bottom-4 sm:-left-4" />

            {/* Clean Image Frame */}
            <div className="relative z-10 overflow-hidden rounded-[28px] border-[10px] border-white bg-white shadow-[0_20px_50px_rgba(100,25,25,0.15)]">

              <div className="group relative h-[420px] overflow-hidden sm:h-[510px] lg:h-[580px] xl:h-[620px]">

                <img
                  src="/director.png"
                  alt="Director of St. Xavier's School"
                  loading="lazy"
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.025]"
                />

              </div>
            </div>

          </div>

        </div>
      </div>
    </section>
  );
};

export default DirectorsDesk;
